import cx from "classnames";
import { FC, useId, useRef } from "react";
import { useIsOverflow } from "../utility/useIsOverflow";
import TooltipWrapper from "@app/shared/TooltipWrapper";

export interface QESRadioButtonProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Smaller text that helps describe the option
   */
  descriptiveText?: string;
  /**
  * Indicates there's a problem with this form field
  */
  error?: boolean;
  /**
   * The label for this particular radio button
   */
  label: string;
  /**
   * ClassName for the label
   */
  labelClass?: string;
  /**
   * Renders the radio labels on the left instead of the right
   */
  labelsOnLeft?: boolean;
  /**
   * An event to trigger when any label is clicked, just in case you want it to do something 
   * different or in addition to the normal onChange handler. If this is just the same as the 
   * onChange handler, you risk it being triggered twice.
   */
  onLabelClick?: (e: React.MouseEvent) => void;
  /**
   * The class to assign to the outer container around each individual label, input, and descriptive text
   */
  radioContainerClassName?: string;
  /**
   * Whether to display the radio button label in a tooltip in the event it overflows its container
   */
  showTooltipOnOverflow?: boolean;
  /**
   * 	The size of the component (`"small"` | `"medium"`). `small` is equivalent to the dense radio styling.
   */
  size?: 'small' | 'medium';
  /**
   * The test id for this radio button. This is used for QA Automation testing.
   */
  testId: string;
}

export const QESRadioButton: FC<QESRadioButtonProps> = (props: QESRadioButtonProps) => {
  const {
    className,
    descriptiveText,
    error,
    label,
    labelClass,
    labelsOnLeft,
    onLabelClick,
    radioContainerClassName,
    size = 'medium',
    showTooltipOnOverflow,
    testId,
    ...otherProps
  } = props;

  const uniqueId = useId();
  const labelRef = useRef<HTMLLabelElement>(null);
  const showTooltip = showTooltipOnOverflow && useIsOverflow(labelRef);

  return (
    <div
      className={cx({
        "qes-radio-group__radio-item": true,
        [radioContainerClassName]: !!props.radioContainerClassName,
      })}
      key={label}
    >
      <TooltipWrapper text={showTooltip ? label : ""}>
        <label
          className={cx({
            "disabled": props.disabled,
            [labelClass]: !!labelClass,
            "qes-radio-group__label": true,
            "qes-radio-group__label--left-label": labelsOnLeft,
            "qes-radio-group__label--small": size === "small",
          })}
          onClick={onLabelClick}
          ref={labelRef}
        >
          {label}
          <input
            className={cx({
              [className]: !!className,
              "error": error,
              "qes-radio": true,
              "qes-radio--left-label": labelsOnLeft,
              "qes-radio--small": size === "small",
            })}
            data-testid={testId}
            {...otherProps}
            type="radio"
            id={uniqueId}
          />
        </label>
      </TooltipWrapper>
      {!!descriptiveText && <label className="qes-radio-group__label label-tiny" htmlFor={uniqueId}>{descriptiveText}</label>}
    </div>
  );
}